import { KnzDiagnostic, RegistryLookup, TextAs } from '../types';
import { RawBlock } from './tokenize';

/** 解析済みブロック（ツリー構築前の中間表現） */
export type ParsedBlock = {
  name: string;
  id?: string;
  /** 解決済み param（text は textAs に応じて string or string[]） */
  params: Record<string, string | string[]>;
  /** children で参照している id の配列 */
  childrenIds: string[];
  startLine: number;
};

export type ParseBlockResult = {
  block: ParsedBlock;
  diagnostics: KnzDiagnostic[];
};

/** `key:` 行かどうか（`key: value` / `key:` の両方） */
const keyLineMatch = (line: string): { key: string; value: string } | null => {
  const idx = line.indexOf(':');
  if (idx === -1) return null;
  const key = line.slice(0, idx).trim();
  // key はインデントなしの識別子想定
  if (!/^[A-Za-z][\w]*$/.test(key)) return null;
  const value = line.slice(idx + 1).trimEnd();
  return { key, value: value.trim() };
};

/**
 * リスト項目行（` - item` / ` -item`）を解析する。
 *
 * 推奨形は `- `（ハイフン + 半角スペース）。後方互換のため `-item`（スペースなし）も
 * 読めるが、`needsSpaceWarning` を true にして呼び出し側で警告を出す。
 *
 * - `value`: 項目の内容。`-` の直後の**半角スペース 1 つだけ**を区切りとして除去する
 *   （それ以降のスペースは内容として保持）。スペースなし `-item` の場合はそのまま。
 * - `needsSpaceWarning`: `-` の直後にスペースが無く、かつ内容が空でない場合に true。
 *   空項目（`-` のみ、または `- `）は段落区切り等に使うため警告しない。
 */
const listItemMatch = (line: string): { value: string; needsSpaceWarning: boolean } | null => {
  const m = line.match(/^\s+-(.*)$/);
  if (!m) return null;
  const rest = m[1];
  if (rest.startsWith(' ')) {
    // 推奨形 `- xxx`：区切りの半角スペース 1 つだけ除去
    return { value: rest.slice(1), needsSpaceWarning: false };
  }
  // スペースなし `-xxx`：内容が空でなければ警告対象
  return { value: rest, needsSpaceWarning: rest !== '' };
};

/**
 * 1 ブロックの本文行を解析し、param・children・id を取り出す。
 *
 * text の解釈は registry の textAs に従う:
 * - raw: `text:` 以降〜ブロック末尾までを無加工の生テキスト（text は最後の param 前提）
 * - array: `text:` 後の `-項目` を配列
 * - joined: `-行` を改行結合した 1 文字列（空項目は空行）
 */
export const parseBlock = (raw: RawBlock, registry: RegistryLookup): ParseBlockResult => {
  const diagnostics: KnzDiagnostic[] = [];
  const params: Record<string, string | string[]> = {};
  let id: string | undefined;
  let childrenIds: string[] = [];

  const textAs: TextAs | undefined = registry.getTextAs(raw.name);
  const lines = raw.lines;

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    // 空行・コメント行（// で始まる）はスキップ
    if (line.trim() === '' || line.trim().startsWith('//')) {
      i++;
      continue;
    }
    const kv = keyLineMatch(line);
    if (!kv) {
      // param 行として解釈できない行は無視（警告）
      diagnostics.push({
        severity: 'warning',
        message: `param として解釈できない行を無視しました: ${line.trim()}`,
        line: raw.startLine + 1 + i,
      });
      i++;
      continue;
    }

    const { key, value } = kv;

    // --- text（raw）: 以降を全部生テキストとして取り込む ---
    if (key === 'text' && textAs === 'raw') {
      const rawLines = lines.slice(i + 1);
      // 先頭と末尾の空行のみ除去（内部は保持）
      params.text = rawLines.join('\n').replace(/^\n+/, '').replace(/\n+$/, '');
      i = lines.length;
      continue;
    }

    // --- 値が空 = 複数行（リスト）値の可能性 ---
    if (value === '') {
      const items: string[] = [];
      let j = i + 1;
      for (; j < lines.length; j++) {
        const item = listItemMatch(lines[j]);
        if (item === null) {
          // 次の key 行 or 非リスト行で終了
          if (lines[j].trim() === '') {
            // ブロック内の空行は複数行値の途中とみなさず終了させる
            break;
          }
          if (keyLineMatch(lines[j])) break;
          break;
        }
        // スペースなし `-xxx` は推奨形 `- xxx` への移行を促す警告を出す
        if (item.needsSpaceWarning) {
          diagnostics.push({
            severity: 'warning',
            message:
              'リスト項目は `- 項目`（ハイフンの後に半角スペース）を推奨します。`-項目` でも動作しますが、スペースを入れてください。',
            line: raw.startLine + 1 + j,
          });
        }
        items.push(item.value);
      }

      if (items.length > 0) {
        if (key === 'text' && textAs === 'joined') {
          params.text = items.join('\n');
        } else if (key === 'children') {
          childrenIds = items.flatMap((it) => it.split(',').map((s) => s.trim())).filter(Boolean);
        } else {
          params[key] = items;
        }
        i = j;
        continue;
      }
      // 空値かつリスト項目なし → 空文字として扱う
      params[key] = '';
      i++;
      continue;
    }

    // --- 単一値 ---
    if (key === 'id') {
      id = value;
    } else if (key === 'children') {
      childrenIds = value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    } else if (key === 'text' && textAs === 'joined') {
      params.text = value;
    } else {
      params[key] = value;
    }
    i++;
  }

  return {
    block: { name: raw.name, id, params, childrenIds, startLine: raw.startLine },
    diagnostics,
  };
};
